import { useMemo } from 'react'

import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'

import { SubscriptionStatus, SubscriptionStatusReason } from '../types'
import { useCreateBillingPortalSession } from '@/features/stripe-payments/api/create-billing-portal-session'

const REASON_COPY: Record<SubscriptionStatusReason, { title: string; description: string; cta?: string }> = {
  ok: {
    title: 'Accès en cours',
    description: 'Merci de patienter pendant la vérification de votre abonnement.',
  },
  practician_included: {
    title: 'Accès illimité',
    description: 'Les praticiens disposent d’un accès gratuit à toutes les fonctionnalités.',
  },
  billing_blocked: {
    title: 'Abonnement suspendu',
    description: 'Votre dernier paiement a échoué. Regularisez votre situation pour reprendre vos activités.',
    cta: 'Gérer mon abonnement',
  },
  trial_expired: {
    title: 'Votre période d’essai est terminée',
    description: 'Souscrivez un abonnement pour continuer à utiliser la plateforme.',
    cta: 'Activer mon abonnement',
  },
  no_subscription: {
    title: 'Souscription requise',
    description: 'Aucune souscription active trouvée. Merci de configurer votre abonnement.',
    cta: 'Configurer mon abonnement',
  },
  missing_profile: {
    title: 'Profil incomplet',
    description: 'Impossible de déterminer votre profil utilisateur. Veuillez réessayer ou contacter le support.',
  },
  not_authenticated: {
    title: 'Session expirée',
    description: 'Reconnectez-vous pour continuer.',
  },
}

export function SubscriptionWall({ status }: { status?: SubscriptionStatus | null }) {
  const {mutate: openBillingPortal, isPending} = useCreateBillingPortalSession()
  const copy = REASON_COPY[status?.reason ?? 'billing_blocked']

  const formattedTrialEnd = useMemo(() => {
    if (!status?.trialEnd) return null
    const date = new Date(status.trialEnd)
    if (Number.isNaN(date.getTime())) return null
    return date.toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' })
  }, [status?.trialEnd])

  const showBillingCta = status?.profile === 'technician' && Boolean(copy.cta)

  const handleOpenBillingPortal = () => {
    openBillingPortal({ return_url: window.location.href })
  }

  return (
    <div className="flex h-full min-h-[60vh] w-full items-center justify-center px-6 py-10">
      <Card className="max-w-xl text-center">
        <CardHeader>
          <CardTitle className="text-2xl font-semibold">{copy.title}</CardTitle>
          <CardDescription className="text-base text-muted-foreground">{copy.description}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {formattedTrialEnd && (
            <p className="text-sm text-muted-foreground">
              Fin de la période d’essai : <strong>{formattedTrialEnd}</strong>
            </p>
          )}
          {showBillingCta && (
            <Button onClick={handleOpenBillingPortal} disabled={isPending} isLoading={isPending} size="lg">
              {copy.cta}
            </Button>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
